import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../axios-orders";
import {
  JointOfferResponse,
  JointOrderOfferStatusResponse,
  OfferAwardedForDeliveryResponse,
  OfferPaginatedResponse,
  OfferRequest,
  OfferResponse,
  UpdateOfferRequest,
} from "../types";
import { queryKeys } from "./queryKeys";

const onGetOffer = async (offerId: string) => {
  const { data } = await axios.get(`/offer/${offerId}`);
  return data;
};

export const useOffer = (offerId: string) =>
  useQuery<OfferResponse>({
    queryKey: [queryKeys.OFFER, offerId],
    queryFn: () => onGetOffer(offerId),
    enabled: !!offerId,
    retry: false,
  });

// `GET /offer/order/{id}/me` (`hasOfferForOrderByLoggedUser`) returns a BARE
// JSON boolean — whether the logged-in seller already has an offer on this
// order — NOT an offer object. Typed accordingly so callers don't reach for
// `.order`/`.id` on it (the Task-5 pinned-bug: the old code did `existingOffer.order`,
// which is always `undefined` on a boolean, so its redirect never fired).
const onGetMyOfferOnOrder = async (orderId: string): Promise<boolean> => {
  try {
    const { data } = await axios.get(`/offer/order/${orderId}/me`);
    return Boolean(data);
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return false;
    }
    throw error;
  }
};

export const useMyOfferOnOrder = (orderId: string, enabled = true) =>
  useQuery<boolean>({
    queryKey: [queryKeys.MY_OFFER_ON_ORDER, orderId],
    queryFn: () => onGetMyOfferOnOrder(orderId),
    enabled: enabled && !!orderId,
    retry: false,
  });

const onGetOffersOnOrders = async (orderId: string, page = 0) => {
  const { data } = await axios.get(`/offer/order/${orderId}/${page}`);
  return data;
};

export const useOffersOnOrders = (orderId: string) =>
  useQuery<OfferPaginatedResponse>({
    queryKey: [queryKeys.OFFERS_ON_ORDERS, orderId],
    queryFn: () => onGetOffersOnOrders(orderId),
    enabled: !!orderId,
    retry: false,
  });

const onGetMultipleOffersOnOrders = async (orderId: string) => {
  const { data } = await axios.get(`/offer/multiple/order/${orderId}`);
  return data;
};

export const useMultipleOffersOnOrders = (multipleOrderId: string) =>
  useQuery<JointOfferResponse>({
    queryKey: [queryKeys.MULTIPLE_OFFERS_ON_ORDERS, multipleOrderId],
    queryFn: () => onGetMultipleOffersOnOrders(multipleOrderId),
    enabled: !!multipleOrderId,
    retry: false,
  });

const onGetMultipleOffers = async (page = 0) => {
  const { data } = await axios.get(`/offer/list/multiple/${page}`);
  return data;
};

export const useMultipleOffers = (page?: number) =>
  useQuery({
    queryKey: [queryKeys.MULTIPLE_OFFERS, page],
    queryFn: () => onGetMultipleOffers(page),
    select: (data) => {
      const items = Object.keys(data).map((key) => data[key]);
      return items;
    },
  });

const onGetUserOffer = async (orderId: string, userId: string) => {
  const { data } = await axios.get(`offer/order/${orderId}/user/${userId}`);
  return data;
};

export const useUserOffer = (orderId: string, userId: string) =>
  useQuery({
    queryKey: [queryKeys.OFFER, orderId, userId],
    queryFn: () => onGetUserOffer(orderId, userId),
    enabled: !!userId,
  });

const onGetMultipleOffer = async (orderId: string) => {
  const { data } = await axios.get(`/offer/multiple/order/user/${orderId}`);
  return data;
};

export const useMultipleOffer = (orderId: string) =>
  useQuery({
    queryKey: [queryKeys.MULTIPLE_OFFER, orderId],
    queryFn: () => onGetMultipleOffer(orderId),
    select: (data: JointOfferResponse) => {
      const offers = Object.keys(data).map((key) => data[key]);
      return offers;
    },
  });

const onCreateOffer = async (body: OfferRequest) => {
  const { data } = await axios.post("/offer", body);
  return data;
};

export const useCreateOffer = (
  onSuccess: (res: any) => void,
  onError: (error?: unknown) => void
) =>
  useMutation({
    mutationFn: onCreateOffer,
    onSuccess,
    onError,
  });

const onCreateMultipleOffer = async (body: any) => {
  const { data } = await axios.post("/offer/multiple", body);
  return data;
};

export const useCreateMultipleOffer = (
  onSuccess: (res: any) => void,
  onError?: (error?: unknown) => void
) =>
  useMutation({
    mutationFn: onCreateMultipleOffer,
    onSuccess,
    onError,
  });

const onAcceptOffer = async (offers: any) => {
  const { data } = await axios.put(`/offer/list/select`, offers);
  return data;
};

export const useAcceptOffer = (onSuccess: () => void, onError: (error?: unknown) => void) =>
  useMutation({
    mutationFn: onAcceptOffer,
    onSuccess,
    onError,
  });

const onUpdateMultipleInverseOffer = async (offers: any) => {
  const { data } = await axios.post(`/offer/updatePrice/list`, offers);
  return data;
};

export const useUpdateMultipleInverseOffer = (
  onSuccess: () => void,
  onError: (error?: unknown) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onUpdateMultipleInverseOffer,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MULTIPLE_OFFER],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.OFFER],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MULTIPLE_OFFERS],
      });
      // A new bid can change this seller's rank on every line it touched.
      queryClient.invalidateQueries({
        queryKey: [queryKeys.POSITION],
      });
      onSuccess();
    },
    onError,
  });
};

const onUpdateInverseOffer = async ({
  offerId,
  price,
}: {
  offerId: string;
  price: string | number;
}) => {
  // Verb hygiene (Task 8): state-changing mutation, now POST (BE dual-maps GET+POST for compat).
  const { data } = await axios.post(`/offer/updatePrice/${offerId}/${price}`);
  return data;
};

export const useUpdateInverseOffer = (
  onSuccess: () => void,
  onError: (error?: unknown) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onUpdateInverseOffer,
    onSuccess: () => {
      onSuccess();
      // Two separate keys: a single [OFFER, POSITION] key matched neither query.
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.OFFER] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.POSITION] }),
      ]);
    },
    onError,
  });
};

const onGetOffers = async (page = 0) => {
  const { data } = await axios.get(`/offer/list/${page}`);
  return data;
};

export const useOffers = (page: number) =>
  useQuery({
    queryKey: [queryKeys.OFFERS, page],
    queryFn: () => onGetOffers(page),
  });

const onGetOffersAwardedForDeliveries = async () => {
  const { data } = await axios.get(`/offer/selected-without-deliveries`);
  return data;
};

export const useOffersAwardedForDeliveries = () =>
  useQuery<OfferAwardedForDeliveryResponse[], Error>({
    queryKey: [queryKeys.OFFERS_AWARDED_FOR_DELIVERIES],
    queryFn: () => onGetOffersAwardedForDeliveries(),
  });

// Joint Order Offer Status
const onGetJointOrderOfferStatus = async (multipleOrderId: string) => {
  const { data } = await axios.get(`/offer/joint-status/${multipleOrderId}`);
  return data;
};

export const useJointOrderOfferStatus = (
  multipleOrderId: string,
  enabled = true
) =>
  useQuery<JointOrderOfferStatusResponse>({
    queryKey: [queryKeys.JOINT_ORDER_OFFER_STATUS, multipleOrderId],
    queryFn: () => onGetJointOrderOfferStatus(multipleOrderId),
    enabled: enabled && !!multipleOrderId,
    retry: false,
  });

// Update Offer Details (for NORMAL/JOINT auctions)
const onUpdateOfferDetails = async (request: UpdateOfferRequest) => {
  const { data } = await axios.put(`/offer/update/${request.offerId}`, request);
  return data;
};

export const useUpdateOfferDetails = (
  onSuccess: (res: OfferResponse) => void,
  onError: (error?: unknown) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onUpdateOfferDetails,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.OFFER],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MULTIPLE_OFFER],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.JOINT_ORDER_OFFER_STATUS],
      });
      onSuccess(data);
    },
    onError,
  });
};

// Decline by order — pre-send "No cotiza" (no existing offer required).
// Declines every line of the (possibly joint) order for the current supplier.
const onDeclineByOrder = async (orderId: string) => {
  const { data } = await axios.post(`/offer/order/${orderId}/decline`);
  return data;
};

export const useDeclineByOrder = (
  onSuccess?: (res: OfferResponse[]) => void,
  onError?: (error?: unknown) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onDeclineByOrder,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.OFFER] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.MY_OFFER_ON_ORDER] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.MULTIPLE_OFFER] });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.JOINT_ORDER_OFFER_STATUS],
      });
      onSuccess?.(data);
    },
    onError,
  });
};

// N10: decline a single line/order of a (joint) quotation, pre-send. Returns the
// declined offer so the caller can undo it via useUndeclineOffer(offer.id).
const onDeclineOrderLine = async (orderId: string) => {
  const { data } = await axios.post(`/offer/order/${orderId}/decline-line`);
  return data;
};

export const useDeclineOrderLine = (
  onSuccess?: (res: OfferResponse) => void,
  onError?: (error?: unknown) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onDeclineOrderLine,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.OFFER] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.MY_OFFER_ON_ORDER] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.MULTIPLE_OFFER] });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.JOINT_ORDER_OFFER_STATUS],
      });
      onSuccess?.(data);
    },
    onError,
  });
};

// Decline Offer (single)
const onDeclineOffer = async (offerId: string) => {
  const { data } = await axios.post(`/offer/${offerId}/decline`);
  return data;
};

export const useDeclineOffer = (
  onSuccess?: (res: OfferResponse) => void,
  onError?: (error?: unknown) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onDeclineOffer,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.OFFER],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MY_OFFER_ON_ORDER],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MULTIPLE_OFFER],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.JOINT_ORDER_OFFER_STATUS],
      });
      onSuccess?.(data);
    },
    onError,
  });
};

// Undecline Offer (single)
const onUndeclineOffer = async (offerId: string) => {
  const { data } = await axios.post(`/offer/${offerId}/undecline`);
  return data;
};

export const useUndeclineOffer = (
  onSuccess?: (res: OfferResponse) => void,
  onError?: (error?: unknown) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onUndeclineOffer,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.OFFER],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MY_OFFER_ON_ORDER],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MULTIPLE_OFFER],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.JOINT_ORDER_OFFER_STATUS],
      });
      onSuccess?.(data);
    },
    onError,
  });
};

// Decline Multiple Offers (batch — joint)
const onDeclineMultipleOffers = async (offerIds: string[]) => {
  const { data } = await axios.post(`/offer/batch/decline`, { offerIds });
  return data;
};

export const useDeclineMultipleOffers = (
  onSuccess?: (res: OfferResponse[]) => void,
  onError?: (error?: unknown) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onDeclineMultipleOffers,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.OFFER],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MY_OFFER_ON_ORDER],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MULTIPLE_OFFER],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.JOINT_ORDER_OFFER_STATUS],
      });
      onSuccess?.(data);
    },
    onError,
  });
};

// Undecline Multiple Offers (batch — joint)
const onUndeclineMultipleOffers = async (offerIds: string[]) => {
  const { data } = await axios.post(`/offer/batch/undecline`, { offerIds });
  return data;
};

export const useUndeclineMultipleOffers = (
  onSuccess?: (res: OfferResponse[]) => void,
  onError?: (error?: unknown) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onUndeclineMultipleOffers,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.OFFER],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MY_OFFER_ON_ORDER],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MULTIPLE_OFFER],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.JOINT_ORDER_OFFER_STATUS],
      });
      onSuccess?.(data);
    },
    onError,
  });
};

// Add Offers to Existing Joint Order
const onAddOffersToJointOrder = async ({
  multipleOrderId,
  offers,
}: {
  multipleOrderId: string;
  offers: OfferRequest[];
}) => {
  const { data } = await axios.post(
    `/offer/multiple/add/${multipleOrderId}`,
    offers
  );
  return data;
};

export const useAddOffersToJointOrder = (
  onSuccess: (res: OfferResponse[]) => void,
  onError: (error?: unknown) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onAddOffersToJointOrder,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MULTIPLE_OFFER],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MULTIPLE_OFFERS],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.JOINT_ORDER_OFFER_STATUS],
      });
      onSuccess(data);
    },
    onError,
  });
};

// Check if user has any offer on a multiple order
const onCheckHasOfferOnMultipleOrder = async (multipleOrderId: string) => {
  const { data } = await axios.get(
    `/offer/multiple/has-offer/${multipleOrderId}`
  );
  return data;
};

export const useHasOfferOnMultipleOrder = (
  multipleOrderId: string,
  enabled = true
) =>
  useQuery<boolean>({
    queryKey: [queryKeys.JOINT_ORDER_OFFER_STATUS, "has-offer", multipleOrderId],
    queryFn: () => onCheckHasOfferOnMultipleOrder(multipleOrderId),
    enabled: enabled && !!multipleOrderId,
    retry: false,
  });
